<template>
  <b-overlay
    :show="loading"
    rounded
    opacity="0.8"
    spinner-small
    spinner-variant="primary"
    class="d-block w-100"
  >
    <div class="partner-referral">
      <modal-confirm-action
        variant="danger"
        refModal="removePartnerReferral"
        idModal="removePartnerReferral"
        :loading="deleting"
        :description="descriptionRemove"
        :title="titleRemove"
        @confirmed="deletePartnerReferral"
      />

      <partner-referral-form-modal ref="formModal" @saved="onFormSaved" />

      <header class="partner-referral-header">
        <div class="partner-referral-header__title-row">
          <i class="fas fa-star partner-referral-header__star"></i>
          <h1 class="partner-referral-header__title">Clube de Benefícios</h1>
          <span class="partner-referral-header__badge">EXCLUSIVO ASSINANTES</span>

          <b-button
            v-if="isAdmin"
            variant="primary"
            class="partner-referral-header__register"
            @click="showForm(null)"
          >
            <i class="fas fa-plus mr-1"></i> Cadastrar parceiro
          </b-button>
        </div>
      </header>

      <p class="partner-referral-description">
        Desfrute de parcerias e descontos exclusivos criados especialmente para
        impulsionar a sua carreira e reduzir os custos do seu escritório. Utilize o
        botão de contato de cada parceiro para resgatar o seu benefício.
      </p>

      <div class="partner-referral-info-banner">
        <i class="fas fa-info-circle"></i>
        <span>Novas parcerias são adicionadas periodicamente. Fique atento às atualizações desta página.</span>
      </div>

      <section v-if="partnerReferrals.length" class="partner-referral-grid">
        <article
          v-for="partnerReferral in partnerReferrals"
          :key="partnerReferral._id"
          class="partner-referral-card"
          :class="{ 'partner-referral-card--inactive': isAdmin && !partnerReferral.isActive }"
        >
          <div v-if="isAdmin" class="partner-referral-card__admin">
            <b-badge v-if="!partnerReferral.isActive" variant="secondary" class="mr-auto">Inativo</b-badge>
            <button class="partner-referral-card__admin-btn" title="Editar" @click="showForm(partnerReferral)">
              <i class="fas fa-pen"></i>
            </button>
            <button class="partner-referral-card__admin-btn text-danger" title="Excluir" @click="showRemove(partnerReferral)">
              <i class="fas fa-trash"></i>
            </button>
          </div>

          <div class="partner-referral-card__logo">
            <img :src="partnerReferral.logoUrl" :alt="partnerReferral.name" />
          </div>

          <div class="partner-referral-card__info">
            <h2 class="partner-referral-card__name">{{ partnerReferral.name }}</h2>
            <p class="partner-referral-card__description">{{ partnerReferral.description }}</p>
          </div>

          <div class="partner-referral-card__benefit">
            <span class="partner-referral-card__benefit-label">{{ partnerReferral.benefitLabel || 'Benefício parceiro' }}</span>
            <span class="partner-referral-card__benefit-condition">{{ partnerReferral.benefitCondition }}</span>
          </div>

          <a
            v-if="partnerReferral.redeemPhone"
            class="partner-referral-card__redeem"
            :href="whatsappLink(partnerReferral.redeemPhone)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="fab fa-whatsapp"></i>
            <span>Resgatar benefício</span>
          </a>
        </article>
      </section>

      <div v-else-if="!loading" class="partner-referral-empty">
        <i class="fas fa-store-slash"></i>
        <p>Nenhum parceiro disponível no momento.</p>
      </div>
    </div>
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ModalConfirmAction from '@/components/ModalConfirmAction.vue'
import PartnerReferralFormModal from '@/components/PartnerReferral/PartnerReferralFormModal.vue'

export default {
  name: 'PartnerReferral',
  components: {
    ModalConfirmAction,
    PartnerReferralFormModal
  },
  data() {
    return {
      loading: false,
      deleting: false,
      descriptionRemove: '',
      titleRemove: '',
      partnerReferralToRemoveId: null
    }
  },
  computed: {
    ...mapGetters('partnerReferral', {
      partnerReferrals: 'partnerReferrals'
    }),
    isAdmin() {
      const user = this.$store.getters['user/current']
      return !!(user && user.role === 'admin')
    }
  },
  async created() {
    this.setBreadcrumb({
      page: 'Comunidade',
      subPage: { name: 'Clube de Benefícios', active: true }
    })
    await this.loadPartnerReferrals()
  },
  methods: {
    ...mapActions('partnerReferral', {
      getActivePartnerReferrals: 'getActivePartnerReferrals',
      getPartnerReferrals: 'getPartnerReferrals',
      handleDeletePartnerReferral: 'handleDeletePartnerReferral'
    }),
    ...mapActions('breadcrumb', {
      setBreadcrumb: 'setBreadcrumb'
    }),
    whatsappLink(phone) {
      const digits = (phone || '').replace(/\D/g, '')
      return `https://wa.me/55${digits}`
    },
    notify(message, variant) {
      this.$bvToast.toast(message, {
        title: 'Aviso',
        variant,
        toaster: 'b-toaster-top-center',
        autoHideDelay: 5000
      })
    },
    async loadPartnerReferrals() {
      this.loading = true
      try {
        if (this.isAdmin) {
          await this.getPartnerReferrals()
        } else {
          await this.getActivePartnerReferrals()
        }
      } catch (e) {
        this.notify('Não foi possível carregar os parceiros do clube de benefícios.', 'danger')
      } finally {
        this.loading = false
      }
    },
    showForm(partnerReferral) {
      this.$refs.formModal.show(partnerReferral)
    },
    async onFormSaved() {
      await this.loadPartnerReferrals()
    },
    showRemove(partnerReferral) {
      this.partnerReferralToRemoveId = partnerReferral._id
      this.titleRemove = 'Excluir parceiro'
      this.descriptionRemove = `Tem certeza que deseja excluir o parceiro: <strong>${partnerReferral.name}</strong>?`
      this.$bvModal.show('removePartnerReferral')
    },
    async deletePartnerReferral() {
      try {
        this.deleting = true
        const response = await this.handleDeletePartnerReferral(this.partnerReferralToRemoveId)
        if (response && response.message) {
          this.notify(response.message, 'success')
          await this.loadPartnerReferrals()
        } else {
          this.notify((response && response.errors) || 'Não foi possível excluir o parceiro.', 'danger')
        }
        this.$bvModal.hide('removePartnerReferral')
      } catch (e) {
        this.notify('Não foi possível excluir o parceiro.', 'danger')
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.partner-referral {
  padding: 8px 16px 40px;
}

.partner-referral-header {
  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__star {
    color: #04a8f3;
    font-size: 20px;
  }

  &__title {
    font-size: 26px;
    font-weight: 700;
    line-height: 31.2px;
    color: #04a8f3;
    margin: 0;
  }

  &__badge {
    background: #cbd63e;
    color: #022245;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.33px;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 4px;
  }

  &__register {
    margin-left: auto;
    border-radius: 4px;
    font-weight: bold;
  }

}

.partner-referral-description {
  color: #5f6368;
  font-size: 14px;
  line-height: 22px;
  width: 100%;
  margin: 16px 0 0;
}

.partner-referral-info-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #e6f5fd;
  color: #5f6368;
  font-size: 13px;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 20px 0 24px;

  i {
    color: #04a8f3;
  }
}

.partner-referral-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
}

.partner-referral-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 1px rgba(2, 34, 69, 0.05);

  &--inactive {
    opacity: 0.6;
  }

  &__admin {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__admin-btn {
    background: transparent;
    border: none;
    color: #5f6368;
    font-size: 14px;
    padding: 4px;
    cursor: pointer;

    &:hover {
      color: #022245;
    }

    &.text-danger:hover {
      color: #dc3545 !important;
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 72px;
    background: #fafcfd;
    border-radius: 6px;
    overflow: hidden;

    img {
      max-width: 100%;
      max-height: 72px;
      object-fit: contain;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__name {
    font-size: 16px;
    font-weight: 700;
    color: #022245;
    margin: 0;
    text-align: center;
  }

  &__description {
    font-size: 13px;
    line-height: 19.5px;
    color: #5f6368;
    margin: 0;
    text-align: justify;
  }

  &__benefit {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: #04a8f3;
    border-radius: 6px;
    padding: 8px 12px;
    text-align: center;
  }

  &__benefit-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
  }

  &__benefit-condition {
    font-size: 13px;
    font-weight: 700;
    color: #ffffff;
  }

  &__redeem {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: auto;
    background: #00c469;
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
    border-radius: 6px;
    padding: 10px 12px;
    transition: background 0.2s ease;

    &:hover {
      background: #00b25f;
      color: #ffffff;
    }

    i {
      font-size: 16px;
    }
  }
}

.partner-referral-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;
  color: #5f6368;

  i {
    font-size: 40px;
    color: #c4c9d0;
  }
}

</style>
